import React from 'react';
import { FiCheck, FiUser, FiMapPin, FiTruck, FiPackage, FiCalendar, FiShield } from 'react-icons/fi';
import type { StepInfo } from '../../types/quote';

interface QuoteStepperProps {
  steps: StepInfo[];
  currentStep: number;
  onStepClick: (stepId: number) => void;
  completedSteps: number[];
}

const STEP_ICONS: Record<number, React.ReactNode> = {
  1: <FiUser className="w-4 h-4 md:w-5 md:h-5" />,
  2: <FiMapPin className="w-4 h-4 md:w-5 md:h-5" />,
  3: <FiTruck className="w-4 h-4 md:w-5 md:h-5" />,
  4: <FiPackage className="w-4 h-4 md:w-5 md:h-5" />,
  5: <FiCalendar className="w-4 h-4 md:w-5 md:h-5" />,
  6: <FiShield className="w-4 h-4 md:w-5 md:h-5" />,
};

export const QuoteStepper: React.FC<QuoteStepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  completedSteps,
}) => {
  const progressPercent = Math.round(((currentStep - 1) / (steps.length - 1)) * 100);

  return (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-8">
      {/* Mobile step status banner */}
      <div className="md:hidden flex items-center justify-between mb-4">
        <div>
          <span className="text-xs uppercase tracking-wider font-semibold text-[#F55109]">
            Step {currentStep} of {steps.length}
          </span>
          <h2 className="text-lg font-bold text-gray-900 special-header">
            {steps[currentStep - 1]?.shortTitle || steps[currentStep - 1]?.name}
          </h2>
        </div>
        <div className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
          {progressPercent}%
        </div>
      </div>

      {/* Progress bar line */}
      <div className="relative w-full h-2 bg-gray-100 rounded-full mb-4 sm:mb-6 overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full bg-[#F55109] transition-all duration-500 ease-out rounded-full"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Step nodes for desktop/tablet */}
      <div className="grid grid-cols-6 gap-2">
        {steps.map((step) => {
          const isCurrent = step.id === currentStep;
          const isCompleted = completedSteps.includes(step.id);
          const isClickable = isCompleted || step.id <= currentStep;

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isClickable && onStepClick(step.id)}
              disabled={!isClickable}
              className={`flex flex-col items-center text-center group transition-all duration-200 ${
                isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
            >
              {/* Circle icon */}
              <div
                className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                  isCurrent
                    ? 'bg-[#F55109] text-white ring-4 ring-[#F55109]/20 shadow-md scale-105'
                    : isCompleted
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                }`}
              >
                {isCompleted ? (
                  <FiCheck className="w-5 h-5 stroke-[2.5]" />
                ) : (
                  STEP_ICONS[step.id] || step.id
                )}
              </div>

              {/* Step Title (hidden on tiny screens, shown on md+) */}
              <div className="mt-2 hidden sm:block">
                <p
                  className={`text-xs font-semibold leading-tight tracking-wide ${
                    isCurrent
                      ? 'text-[#F55109]'
                      : isCompleted
                      ? 'text-gray-800'
                      : 'text-gray-400'
                  }`}
                >
                  {step.shortTitle}
                </p>
                <span className="text-[10px] text-gray-400 block mt-0.5">
                  Step {step.id}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
